/**
 * Shared TTS playback engine (module singleton). One utterance at a time; its
 * segments play in FIFO order. Progressive playback via MediaSource when the
 * browser can play audio/mpeg through it, else a buffered-blob <audio> fallback
 * (Safari). Autoplay is unlocked on the first user gesture, à la callAudio.ts.
 */

type Status = "idle" | "playing";
type Sink = (
  response: Response,
  ctx: { signal: AbortSignal; onBlob?: (b: Blob) => void },
) => Promise<void>;

interface Segment {
  id: string;
  response: Response;
}

let activeId: string | null = null;
let status: Status = "idle";
let queue: Segment[] = [];
let draining = false;
let endedIds = new Set<string>();
let gen = 0; // bumped on stop() — in-flight loops check it to bail out
let controller: AbortController | null = null;
const subscribers = new Set<() => void>();

// Cached snapshot for useSyncExternalStore — getSnapshot must return a
// reference-stable value unless the state actually changed, or React treats
// every render as "changed" and loops. Rebuilt only inside setState().
let snapshot: { activeId: string | null; status: Status } = { activeId: null, status: "idle" };

function notify(): void {
  for (const cb of subscribers) cb();
}

function setState(next: { activeId?: string | null; status?: Status }): void {
  if (next.activeId !== undefined) activeId = next.activeId;
  if (next.status !== undefined) status = next.status;
  snapshot = { activeId, status };
  notify();
}

// ── default sink: MSE progressive, blob fallback ─────────────────────────────

function mseSupported(): boolean {
  return (
    typeof MediaSource !== "undefined" &&
    typeof MediaSource.isTypeSupported === "function" &&
    MediaSource.isTypeSupported("audio/mpeg")
  );
}

let defaultSink: Sink = async (response, { signal, onBlob }) => {
  const audio = new Audio();
  const chunks: Uint8Array[] = [];

  const finished = new Promise<void>((resolve, reject) => {
    audio.onended = () => resolve();
    audio.onerror = () => reject(new Error("audio playback error"));
    signal.addEventListener("abort", () => {
      audio.pause();
      resolve();
    });
  });

  if (mseSupported() && response.body) {
    const mediaSource = new MediaSource();
    audio.src = URL.createObjectURL(mediaSource);
    await new Promise<void>((r) => (mediaSource.onsourceopen = () => r()));
    const sb = mediaSource.addSourceBuffer("audio/mpeg");
    const reader = response.body.getReader();
    const append = (buf: Uint8Array) =>
      new Promise<void>((resolve) => {
        sb.onupdateend = () => resolve();
        sb.appendBuffer(buf as BufferSource);
      });
    void audio.play().catch(() => {});
    for (;;) {
      const { done, value } = await reader.read();
      if (done || signal.aborted) break;
      chunks.push(value);
      await append(value);
    }
    if (!signal.aborted) mediaSource.endOfStream();
  } else {
    const blob = await response.blob();
    chunks.push(new Uint8Array(await blob.arrayBuffer()));
    audio.src = URL.createObjectURL(blob);
    void audio.play().catch(() => {});
  }

  await finished;
  if (onBlob) onBlob(new Blob(chunks as BlobPart[], { type: "audio/mpeg" }));
};

// ── queue drain loop ─────────────────────────────────────────────────────────

async function drain(): Promise<void> {
  if (draining) return;
  draining = true;
  const myGen = gen;

  while (queue.length > 0) {
    if (gen !== myGen) break; // stop() happened
    const seg = queue.shift()!;
    controller = new AbortController();
    try {
      await defaultSink(seg.response, { signal: controller.signal });
    } catch {
      /* a failed segment shouldn't wedge the queue */
    }
    if (gen !== myGen) break;
  }

  if (gen !== myGen) return; // superseded — leave draining/state to the live loop
  draining = false;
  if (queue.length === 0 && endedIds.has(activeId ?? "")) {
    setState({ activeId: null, status: "idle" });
  }
}

export const ttsPlayer = {
  startUtterance(id: string): void {
    this.stop();
    queue = [];
    endedIds = new Set();
    setState({ activeId: id, status: "playing" });
  },

  enqueue(id: string, response: Response): void {
    if (id !== activeId) return; // stale / barge-in
    queue.push({ id, response });
    void drain();
  },

  endUtterance(id: string): void {
    if (id !== activeId) return;
    endedIds.add(id);
    if (!draining && queue.length === 0) {
      setState({ activeId: null, status: "idle" });
    }
  },

  stop(): void {
    gen += 1;
    queue = [];
    controller?.abort();
    controller = null;
    draining = false;
    setState({ activeId: null, status: "idle" });
  },

  subscribe(cb: () => void): () => void {
    subscribers.add(cb);
    return () => subscribers.delete(cb);
  },

  getState(): { activeId: string | null; status: Status } {
    return snapshot;
  },

  installAutoplayPrimer(): void {
    if (typeof window === "undefined") return;
    const events = ["pointerdown", "keydown", "touchstart"] as const;
    const onGesture = () => {
      for (const t of events) window.removeEventListener(t, onGesture, true);
      const a = new Audio();
      a.muted = true;
      a.play().then(() => a.pause(), () => {});
    };
    for (const t of events) {
      window.addEventListener(t, onGesture, { capture: true, passive: true });
    }
  },

  /** Test seam — swap the audio sink. */
  __setSink(fn: Sink): void {
    defaultSink = fn;
  },
};
